// Creative Studio upload presentation contract.
//
// WHY THIS EXISTS
// ---------------
// The direct-to-storage rewrite made large uploads WORK. It did not make them
// READ. A 716.6MB transfer surfaced as a disabled button reading
//
//   Uploading 0/1…
//
// which rendered in the Acquire face as "UPLOADING OF1..." — malformed copy on
// top of a state model the user could not act on. Worse, the byte transfer and
// the server-side work that follows it were presented as one undifferentiated
// "upload", so a file that reached 100% and then sat in verification looked
// frozen.
//
// Everything a person reads during an upload is decided HERE, as pure
// functions over observable facts (phase, bytes, elapsed time, failure code).
// Nothing in this module renders, fetches, or guesses:
//
//   * a percentage is only ever derived from real transferred bytes;
//   * a stage that is not byte-measurable reports NO percentage rather than a
//     fabricated one;
//   * an ETA is withheld until enough transfer history exists to be stable;
//   * failure copy comes from a curated table, so a provider's raw error text
//     can never reach a user.
//
// This module is framework-free and byte-identical across Quantum and Cortex.

import { UPLOAD_PHASES, formatBytes } from "./creativeUploadClient";
import { describeConfirmedProgress } from "./creativeUploadMessages";

/**
 * The stages a creative moves through, in order.
 *
 * These are USER-FACING stages, not transport internals. Each one exists
 * because a person waiting on a multi-minute upload can distinguish it from
 * its neighbours: bytes are moving, or the server is checking what it
 * received, or the library is being updated, or inspection is running.
 */
export const UPLOAD_STAGES = Object.freeze({
  QUEUED: "queued",
  PREPARING: "preparing",
  RESUMING: "resuming",
  UPLOADING: "uploading",
  VERIFYING: "verifying",
  FINALIZING: "finalizing",
  INSPECTING: "inspecting",
  COMPLETE: "complete",
  FAILED: "failed",
});

/** Presentation order, used for sorting and for the progression tests. */
export const UPLOAD_STAGE_ORDER = Object.freeze([
  UPLOAD_STAGES.QUEUED,
  UPLOAD_STAGES.PREPARING,
  UPLOAD_STAGES.RESUMING,
  UPLOAD_STAGES.UPLOADING,
  UPLOAD_STAGES.VERIFYING,
  UPLOAD_STAGES.FINALIZING,
  UPLOAD_STAGES.INSPECTING,
  UPLOAD_STAGES.COMPLETE,
]);

/**
 * ONLY this stage has a truthful percentage.
 *
 * Everything else is server-side work of unknown duration. Continuing a
 * determinate bar into those stages would mean inventing progress, which is
 * precisely the "stuck at 99%" experience this replaces.
 */
export function isByteMeasurable(stage) {
  return stage === UPLOAD_STAGES.UPLOADING || stage === UPLOAD_STAGES.RESUMING;
}

/** A stage that is doing work right now (so the card animates). */
export function isActiveStage(stage) {
  return (
    stage !== UPLOAD_STAGES.COMPLETE &&
    stage !== UPLOAD_STAGES.FAILED &&
    stage !== UPLOAD_STAGES.QUEUED
  );
}

export function isTerminalStage(stage) {
  return stage === UPLOAD_STAGES.COMPLETE || stage === UPLOAD_STAGES.FAILED;
}

/**
 * Transport phase -> user-facing stage.
 *
 * The upload client reports what the network is doing. It has no view of the
 * library refresh or of server-side inspection, so those two stages are set by
 * the page once it observes them; this mapper covers everything the transport
 * itself can see.
 */
export function stageForPhase(phase) {
  switch (phase) {
    case UPLOAD_PHASES.QUEUED:
      return UPLOAD_STAGES.QUEUED;
    case UPLOAD_PHASES.PREPARING:
      return UPLOAD_STAGES.PREPARING;
    case UPLOAD_PHASES.RESUMING:
      return UPLOAD_STAGES.RESUMING;
    case UPLOAD_PHASES.UPLOADING:
      return UPLOAD_STAGES.UPLOADING;
    case UPLOAD_PHASES.VERIFYING:
    case UPLOAD_PHASES.FINALIZING:
      return UPLOAD_STAGES.VERIFYING;
    case UPLOAD_PHASES.DONE:
      return UPLOAD_STAGES.COMPLETE;
    case UPLOAD_PHASES.FAILED:
      return UPLOAD_STAGES.FAILED;
    default:
      return UPLOAD_STAGES.QUEUED;
  }
}

/**
 * Server-side intake status -> the stage a finished upload is really in.
 *
 * A stored creative is not yet an inspected creative. Saying "ready" before
 * inspection resolves would be the same lie the byte bar used to tell.
 */
export function stageForIntakeStatus(status) {
  const value = String(status || "").toLowerCase();
  if (!value) return UPLOAD_STAGES.COMPLETE;
  if (value === "received" || value === "storing" || value === "stored" || value === "processing") {
    return UPLOAD_STAGES.INSPECTING;
  }
  // processed / needs_metadata / processing_failed / quarantined /
  // legacy_unprocessed are all settled as far as UPLOADING is concerned — the
  // asset's own governed state takes over from here.
  return UPLOAD_STAGES.COMPLETE;
}

/** True once inspection has actually run, which is what "ready" claims. */
export function isInspectionComplete(status) {
  return String(status || "").toLowerCase() === "processed";
}

// ---------------------------------------------------------------------------
// Stage transitions
//
// The transport reports its own phases; the two stages AFTER it — registering
// the creative with the library, and waiting on inspection — are observed by
// the page. Both applications drive them through these helpers so the two
// portals cannot diverge in when a stage begins.
// ---------------------------------------------------------------------------

/**
 * Move the named entries to a stage, leaving the rest untouched.
 *
 * A failed file is never advanced: it has reached its terminal state and must
 * keep showing its own reason.
 */
export function withStage(entries, stage, { names = null } = {}) {
  const wanted = names ? new Set(names.map((name) => String(name))) : null;
  return (Array.isArray(entries) ? entries : []).map((entry) => {
    if (!entry || entry.stage === UPLOAD_STAGES.FAILED) return entry;
    if (wanted && !wanted.has(String(entry.name))) return entry;
    return { ...entry, stage };
  });
}

/**
 * Resolve each successfully uploaded entry against its server-side intake
 * status: still being inspected, or settled.
 *
 * `statusFor` is supplied by the caller so this stays pure and testable.
 */
export function withIntakeStages(entries, statusFor) {
  return (Array.isArray(entries) ? entries : []).map((entry) => {
    if (!entry || entry.stage === UPLOAD_STAGES.FAILED) return entry;
    if (!entry.assetId) return entry;
    const status = typeof statusFor === "function" ? statusFor(entry) : null;
    if (!status) return entry;
    return { ...entry, stage: stageForIntakeStatus(status), intakeStatus: status };
  });
}

/** True once nothing in the batch is still moving. */
export function isBatchSettled(entries) {
  const list = (Array.isArray(entries) ? entries : []).filter(Boolean);
  return list.length > 0 && list.every((entry) => isTerminalStage(entry.stage));
}

// ---------------------------------------------------------------------------
// Stage copy
//
// One headline, one supporting sentence. The headline is the stage; the
// sentence says what Cphere is doing in plain terms. No storage provider, no
// session, no checksum, no worker, no chunk — those are implementation, and a
// person uploading a cutdown has no use for them.
// ---------------------------------------------------------------------------

const SINGLE_STAGE_COPY = Object.freeze({
  [UPLOAD_STAGES.QUEUED]: {
    headline: "QUEUED",
    detail: "Waiting for an upload slot.",
  },
  [UPLOAD_STAGES.PREPARING]: {
    headline: "PREPARING CREATIVE",
    detail: "Validating your creative and preparing a secure upload.",
  },
  [UPLOAD_STAGES.RESUMING]: {
    headline: "RESUMING UPLOAD",
    detail: "Continuing from your previous transfer.",
  },
  [UPLOAD_STAGES.UPLOADING]: {
    headline: "UPLOADING CREATIVE",
    detail: "Securely transferring your creative to Cphere storage.",
  },
  [UPLOAD_STAGES.VERIFYING]: {
    headline: "VERIFYING UPLOAD",
    detail: "Transfer complete. Cphere is verifying the uploaded creative.",
  },
  [UPLOAD_STAGES.FINALIZING]: {
    headline: "FINALIZING CREATIVE",
    detail: "Registering your creative with Creative Studio.",
  },
  [UPLOAD_STAGES.INSPECTING]: {
    headline: "INSPECTING CREATIVE",
    detail: "Cphere is validating the file and preparing it for review.",
  },
  [UPLOAD_STAGES.COMPLETE]: {
    headline: "UPLOAD COMPLETE",
    detail: "Your creative has been added to Creative Studio.",
  },
  [UPLOAD_STAGES.FAILED]: {
    headline: "UPLOAD FAILED",
    detail: "Cphere could not complete this upload. Please try again.",
  },
});

/**
 * The headline/detail pair for one creative at one stage.
 *
 * `count` describes the batch this creative belongs to; only the actively
 * transferring stage phrases the batch, because that is the only stage where
 * "6 creatives" is more useful than the individual file's own state.
 */
export function describeUploadStage(stage, { count = 1 } = {}) {
  const copy = SINGLE_STAGE_COPY[stage] || SINGLE_STAGE_COPY[UPLOAD_STAGES.QUEUED];
  if (count > 1 && stage === UPLOAD_STAGES.UPLOADING) {
    return {
      headline: `UPLOADING ${count} CREATIVES`,
      detail: "Cphere is securely uploading your selected creatives.",
    };
  }
  return { ...copy };
}

/** Compact per-row label for a batch: "UPLOADING · 63%", "QUEUED", "COMPLETE". */
export function describeRowStatus(entry) {
  const stage = entry?.stage || UPLOAD_STAGES.QUEUED;
  if (stage === UPLOAD_STAGES.FAILED) return "UPLOAD FAILED";
  if (stage === UPLOAD_STAGES.COMPLETE) return "COMPLETE";
  const percent = percentFor(entry);
  const label = describeUploadStage(stage).headline.replace(/ CREATIVES?$/, "").replace(/^UPLOAD /, "");
  return percent === null ? label : `${label} · ${percent}%`;
}

// ---------------------------------------------------------------------------
// Byte arithmetic
// ---------------------------------------------------------------------------

/**
 * Transfer-time byte formatting: always one decimal, so a running counter does
 * not jitter between "716 MB" and "716.6 MB" as it climbs.
 *
 * The DIVISOR must stay binary, matching `formatBytes` in the upload client.
 * Drive shows the 751,399,812-byte production master as "716.6 MB"; a decimal
 * formatter calls the same object "751.4 MB". Those two numbers appearing in
 * one interface — one in the progress line, one in the limit and "already
 * saved" copy — would read as two different files.
 *
 * `formatBytes` remains the authority for LIMIT labels ("2 GB", not "2.0 GB");
 * only the decimal place differs between them.
 */
export function formatTransferBytes(bytes) {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric < 0) return "";
  if (numeric < 1024) return `${Math.trunc(numeric)} bytes`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = numeric / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
}

/** "129.0 MB of 716.6 MB" — the metadata line under the bar. */
export function formatTransferred(loaded, total) {
  const totalBytes = Number(total);
  if (!Number.isFinite(totalBytes) || totalBytes <= 0) return "";
  const loadedBytes = Math.min(Math.max(Number(loaded) || 0, 0), totalBytes);
  return `${formatTransferBytes(loadedBytes)} of ${formatTransferBytes(totalBytes)}`;
}

/**
 * The percentage, or null when there is no honest one to give.
 *
 * Returning null (rather than a number) is what lets the component switch to
 * an indeterminate treatment instead of parking a determinate bar at 99%.
 */
export function percentFor(entry) {
  if (!entry) return null;
  const stage = entry.stage || UPLOAD_STAGES.QUEUED;
  if (stage === UPLOAD_STAGES.COMPLETE) return 100;
  if (!isByteMeasurable(stage)) return null;
  const total = Number(entry.total);
  const loaded = Number(entry.loaded);
  if (!Number.isFinite(total) || total <= 0) return null;
  if (!Number.isFinite(loaded) || loaded <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor((loaded / total) * 100)));
}

// ---------------------------------------------------------------------------
// Rate and ETA
//
// An ETA that jumps between "4 min" and "40 sec" is worse than no ETA: it
// teaches the user to distrust the interface during the exact wait it was
// added to make bearable. So the estimate is withheld until the samples
// support it, and it is always averaged over a window rather than taken from
// the last two events.
// ---------------------------------------------------------------------------

/** Below these, an estimate is noise rather than information. */
const MIN_SAMPLES = 3;
const MIN_ELAPSED_MS = 2500;
const MIN_BYTES_MOVED = 2 * 1000 * 1000;

/** How many recent samples the average is taken over. */
export const TRANSFER_SAMPLE_WINDOW = 12;

/**
 * Append a progress observation, keeping only the recent window.
 *
 * Pure: the caller owns the array, so this is trivially testable without
 * timers.
 */
export function recordTransferSample(samples, { at, loaded }) {
  const list = Array.isArray(samples) ? samples : [];
  const time = Number(at);
  const bytes = Number(loaded);
  if (!Number.isFinite(time) || !Number.isFinite(bytes)) return list;
  const last = list[list.length - 1];
  // Ignore non-advancing repeats: they would drag the average toward zero.
  if (last && (time <= last.at || bytes < last.loaded)) return list;
  return [...list, { at: time, loaded: bytes }].slice(-TRANSFER_SAMPLE_WINDOW);
}

/**
 * Average throughput and remaining time, or null when the history is too thin.
 *
 * Deliberately conservative — three separate gates must all pass.
 */
export function estimateTransfer(samples, { total } = {}) {
  const list = (Array.isArray(samples) ? samples : []).filter(
    (sample) => sample && Number.isFinite(sample.at) && Number.isFinite(sample.loaded)
  );
  if (list.length < MIN_SAMPLES) return null;

  const first = list[0];
  const last = list[list.length - 1];
  const elapsedMs = last.at - first.at;
  const movedBytes = last.loaded - first.loaded;
  if (elapsedMs < MIN_ELAPSED_MS) return null;
  if (movedBytes < MIN_BYTES_MOVED) return null;

  const bytesPerSecond = movedBytes / (elapsedMs / 1000);
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return null;

  const totalBytes = Number(total);
  const remainingBytes =
    Number.isFinite(totalBytes) && totalBytes > last.loaded ? totalBytes - last.loaded : 0;
  const secondsRemaining = remainingBytes > 0 ? remainingBytes / bytesPerSecond : 0;

  return { bytesPerSecond, secondsRemaining };
}

/** "8.4 MB/s". Empty when there is nothing dependable to report. */
export function formatTransferRate(bytesPerSecond) {
  const rate = Number(bytesPerSecond);
  if (!Number.isFinite(rate) || rate <= 0) return "";
  return `${formatTransferBytes(rate)}/s`;
}

/**
 * "About 3 min remaining".
 *
 * Coarse on purpose: rounding to the nearest useful unit is what keeps the
 * number from visibly twitching between renders.
 */
export function formatTimeRemaining(secondsRemaining) {
  const seconds = Number(secondsRemaining);
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  if (seconds < 45) return "Less than a minute remaining";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `About ${minutes} min remaining`;
  const hours = Math.round(seconds / 3600);
  return `About ${hours} hr remaining`;
}

// ---------------------------------------------------------------------------
// Batch summary
// ---------------------------------------------------------------------------

/**
 * Overall batch state, weighted by ACTUAL BYTES.
 *
 * A simple "3 of 6" would show a 275MB file and a 716.6MB file as equal
 * progress, which is why the bar is computed from the byte sums instead. The
 * "n of m creatives" count remains alongside it, because that is the thing a
 * person actually asked for.
 */
export function summarizeUploadBatch(entries) {
  const list = (Array.isArray(entries) ? entries : []).filter(Boolean);
  const total = list.length;
  if (!total) {
    return {
      total: 0,
      completed: 0,
      failed: 0,
      active: 0,
      percent: null,
      countLabel: "",
      transferredLabel: "",
      headline: "",
      detail: "",
    };
  }

  const completed = list.filter((entry) => entry.stage === UPLOAD_STAGES.COMPLETE).length;
  const failed = list.filter((entry) => entry.stage === UPLOAD_STAGES.FAILED).length;
  const active = list.filter((entry) => isActiveStage(entry.stage)).length;

  const totalBytes = list.reduce((sum, entry) => sum + (Number(entry.total) || 0), 0);
  const loadedBytes = list.reduce((sum, entry) => {
    const entryTotal = Number(entry.total) || 0;
    // A finished file counts its whole size even though its byte events
    // stopped arriving, otherwise the bar would slip backwards on completion.
    if (entry.stage === UPLOAD_STAGES.COMPLETE) return sum + entryTotal;
    if (entry.stage === UPLOAD_STAGES.FAILED) return sum;
    return sum + Math.min(Math.max(Number(entry.loaded) || 0, 0), entryTotal);
  }, 0);

  const percent =
    totalBytes > 0 ? Math.max(0, Math.min(100, Math.floor((loadedBytes / totalBytes) * 100))) : null;

  const { headline, detail } = describeBatchOutcome({ total, completed, failed, active });

  return {
    total,
    completed,
    failed,
    active,
    percent,
    countLabel: `${completed} OF ${total} CREATIVES UPLOADED`,
    transferredLabel: totalBytes > 0 ? `${formatTransferred(loadedBytes, totalBytes)} transferred` : "",
    headline,
    detail,
  };
}

/**
 * The batch banner.
 *
 * Partial success is stated as partial and the successes are named first —
 * the same honesty rule the batch error copy already follows.
 */
export function describeBatchOutcome({ total, completed, failed, active }) {
  if (active > 0) {
    return {
      headline: total === 1 ? "UPLOADING CREATIVE" : `UPLOADING ${total} CREATIVES`,
      detail:
        total === 1
          ? "Securely transferring your creative to Cphere storage."
          : "Cphere is securely uploading your selected creatives.",
    };
  }
  if (!failed && completed === total) {
    return {
      headline: total === 1 ? "CREATIVE UPLOADED" : `${total} CREATIVES UPLOADED`,
      detail:
        total === 1
          ? "Your creative has been added to Creative Studio and is being prepared for review."
          : "All selected creatives were successfully added to Creative Studio.",
    };
  }
  if (completed > 0 && failed > 0) {
    return {
      headline: `${completed} OF ${total} CREATIVES UPLOADED`,
      detail: `${failed} ${failed === 1 ? "creative" : "creatives"} could not be uploaded. Your completed uploads are safe.`,
    };
  }
  if (failed > 0) {
    return {
      headline: total === 1 ? "UPLOAD FAILED" : `${total} CREATIVES COULD NOT BE UPLOADED`,
      detail: "Cphere could not complete these uploads. Please try again.",
    };
  }
  return { headline: "", detail: "" };
}

/** The single-creative success banner, split by whether inspection has run. */
export function describeSingleSuccess({ inspected = false } = {}) {
  return inspected
    ? { headline: "CREATIVE READY", detail: "Your creative is ready in Creative Studio." }
    : {
        headline: "CREATIVE UPLOADED",
        detail: "Your creative has been added to Creative Studio and is being prepared for review.",
      };
}

// ---------------------------------------------------------------------------
// Failure copy
//
// Curated by code. A provider's own error text NEVER reaches this layer's
// output unless it passes `isSafeDetail`, so a Drive 403 body, a stack frame,
// or a credential fragment cannot be rendered by accident.
// ---------------------------------------------------------------------------

/**
 * Tokens that mark a string as implementation rather than product language.
 * Presence of any one of them disqualifies a server sentence from display.
 */
const LEAKY_TOKENS = [
  "google",
  "drive",
  "gapi",
  "googleapis",
  "oauth",
  "bearer",
  "token",
  "credential",
  "service account",
  "sha-256",
  "sha256",
  "md5",
  "checksum",
  "resumable",
  "session uri",
  "upload session",
  "chunk",
  "multer",
  "sequelize",
  "postgres",
  "econnreset",
  "enotfound",
  "etimedout",
  "stack",
  "traceback",
  "at object.",
  "error:",
  "exception",
  "undefined is not",
  "null is not",
  "http 4",
  "http 5",
  "status code",
];

/**
 * Is this sentence safe to show a user verbatim?
 *
 * Fail-closed: anything that smells like transport, storage, or a runtime
 * exception is refused, and the curated fallback is used instead.
 */
export function isSafeDetail(message) {
  const text = String(message || "").trim();
  if (!text) return false;
  if (text.length > 220) return false;
  if (/https?:\/\//i.test(text)) return false;
  if (/[{}<>]|\bat\s+\/|\.js:\d+/.test(text)) return false;
  const lower = text.toLowerCase();
  return !LEAKY_TOKENS.some((token) => lower.includes(token));
}

const FAILURE_COPY = Object.freeze({
  creative_upload_transport_failed: {
    headline: "UPLOAD INTERRUPTED",
    detail: "The connection was interrupted. Your upload can resume from where it stopped.",
    action: "RESUME UPLOAD",
  },
  creative_upload_session_expired: {
    headline: "UPLOAD SESSION EXPIRED",
    detail: "Cphere needs to reconnect this creative before continuing.",
    action: "RETRY UPLOAD",
  },
  // #252's interruption family. These are failures of the CONNECTION, not of
  // the file, and the bytes storage already confirmed survive them — so the
  // copy never claims a loss the provider did not report.
  creative_upload_interrupted_resumable: {
    headline: "UPLOAD INTERRUPTED",
    detail: "The connection was interrupted. Your upload can resume from where it stopped.",
    action: "RESUME UPLOAD",
  },
  creative_upload_interrupted_empty: {
    headline: "UPLOAD INTERRUPTED",
    detail: "The connection was interrupted before the upload could be saved. You can safely try again.",
    action: "RETRY UPLOAD",
  },
  creative_upload_interrupted_unknown: {
    headline: "UPLOAD INTERRUPTED",
    // Not knowing is not the same as knowing nothing was saved.
    detail:
      "The connection was interrupted and Cphere could not confirm what was saved. Resuming will continue from wherever storage left off.",
    action: "RESUME UPLOAD",
  },
  creative_upload_file_too_large: {
    headline: "FILE EXCEEDS UPLOAD LIMIT",
    detail: "This file is larger than Cphere currently supports.",
    action: null,
    // The server's sentence names the real size and the real limit, which is
    // strictly more useful — allow it through when it is safe.
    preferServerDetail: true,
  },
  creative_upload_unsupported_extension: {
    headline: "UNSUPPORTED FILE FORMAT",
    detail: "Cphere currently supports MOV and MP4 video creatives for this upload.",
    action: null,
    preferServerDetail: true,
  },
  creative_upload_empty_file: {
    headline: "FILE IS EMPTY",
    detail: "This file contains no data and cannot be uploaded.",
    action: null,
    preferServerDetail: true,
  },
  creative_upload_missing_filename: {
    headline: "FILE NAME MISSING",
    detail: "This file has no name, so it cannot be uploaded.",
    action: null,
  },
  creative_upload_finalize_failed: {
    // The bytes ARE in storage; only the server-side completion step failed.
    // Telling the user to retry would ask them to re-send a 716.6 MB file
    // that is already saved — the precise falsehood #252 removed.
    headline: "UPLOAD SAVED, NOT YET COMPLETED",
    detail:
      "Your file finished uploading but Cphere could not complete it. The upload is safe in storage; finishing will not re-send it.",
    action: "FINISH UPLOAD",
  },
  creative_upload_verification_failed_legacy: {
    headline: "UPLOAD COULD NOT BE VERIFIED",
    detail:
      "The transfer completed, but Cphere could not verify the stored creative. Please retry the upload.",
    action: "RETRY UPLOAD",
  },
  creative_upload_verification_failed: {
    headline: "UPLOAD COULD NOT BE VERIFIED",
    detail:
      "The transfer completed, but Cphere could not verify the stored creative. Please retry the upload.",
    action: "RETRY UPLOAD",
  },
  creative_upload_rejected_by_storage: {
    headline: "UPLOAD COULD NOT BE VERIFIED",
    detail:
      "The transfer completed, but Cphere could not verify the stored creative. Please retry the upload.",
    action: "RETRY UPLOAD",
  },
  creative_upload_no_capability: {
    headline: "UPLOAD COULD NOT START",
    detail: "Cphere could not open a secure upload for this creative. Please try again.",
    action: "RETRY UPLOAD",
  },
  creative_upload_session_failed: {
    headline: "UPLOAD COULD NOT START",
    detail: "Cphere could not open a secure upload for this creative. Please try again.",
    action: "RETRY UPLOAD",
  },
  creative_upload_cancelled: {
    headline: "UPLOAD CANCELLED",
    detail: "This upload was cancelled. Nothing partial was saved.",
    action: "RETRY UPLOAD",
  },
});

const UNKNOWN_FAILURE = Object.freeze({
  headline: "UPLOAD FAILED",
  detail: "Cphere could not complete this upload. Please try again.",
  action: "RETRY UPLOAD",
});

/**
 * Product-language failure copy for one file.
 *
 * An UNRECOGNISED code always falls back to the generic sentence — the server
 * message is never trusted for codes we have not curated, because an unknown
 * code is exactly the case where a raw provider string could appear.
 */
/**
 * Which button to offer, decided by EVIDENCE where evidence exists.
 *
 * #252 made the transport report `action` from what storage actually
 * confirmed, rather than inferring it from the fact that an error occurred.
 * That verdict outranks this module's static table, because the table only
 * knows the error class while the transport knows what survived. The table
 * still covers the pre-transfer rejections (too large, wrong format), which
 * never reach storage and therefore carry no action at all.
 */
function actionFor(failure, entry) {
  const reported = String(failure?.action || "").toLowerCase();
  if (reported === "resume") return entry?.action === "FINISH UPLOAD" ? "FINISH UPLOAD" : "RESUME UPLOAD";
  if (reported === "retry") return "RETRY UPLOAD";
  return entry ? entry.action : UNKNOWN_FAILURE.action;
}

export function describeUploadFailure(failure) {
  const code = String(failure?.code || "").trim();
  const entry = FAILURE_COPY[code];
  if (!entry) {
    return {
      ...UNKNOWN_FAILURE,
      // Even an unrecognised code keeps a transport-reported action: the
      // server's read of what storage holds is trustworthy regardless of
      // whether this table has heard of the code.
      action: actionFor(failure, null),
      progressNote: describeConfirmedProgress(failure, formatBytes),
      code: code || null,
    };
  }

  const serverDetail = failure?.message;
  const detail =
    entry.preferServerDetail && isSafeDetail(serverDetail) ? String(serverDetail).trim() : entry.detail;

  return {
    headline: entry.headline,
    detail,
    action: actionFor(failure, entry),
    // "612.5 MB of 716.6 MB already saved." Built by #252's own helper, so
    // resume is stated as a durable fact rather than a promise.
    progressNote: describeConfirmedProgress(failure, formatBytes),
    code,
  };
}

/** Batch-level retry affordance; only failed files are ever re-sent. */
export function describeRetryAction(failedCount) {
  if (!failedCount) return "";
  return failedCount === 1 ? "RETRY UPLOAD" : "RETRY FAILED UPLOADS";
}

/**
 * The primary submit control's copy.
 *
 * While a batch is in flight the control stops pretending to be a button —
 * the old disabled "Uploading 0/1…" was both unreadable and unactionable.
 */
export function describeUploadAction({ pendingCount = 0, active = false, failedCount = 0 } = {}) {
  if (active) return "UPLOAD IN PROGRESS";
  if (failedCount > 0) return describeRetryAction(failedCount);
  if (pendingCount > 1) return `UPLOAD ${pendingCount} CREATIVES`;
  if (pendingCount === 1) return "UPLOAD CREATIVE";
  return "SELECT FILES";
}

/**
 * The drop-zone's supporting sentence before a transfer starts.
 *
 * Says where the creative is going in PRODUCT terms. The previous copy named
 * the storage provider's folder, which is an implementation detail the client
 * never needs and cannot act on.
 */
export function describeDropZone({ pendingCount = 0 } = {}) {
  if (pendingCount > 1) {
    return "Each file becomes its own creative in the selected advertiser's Creative Studio.";
  }
  if (pendingCount === 1) {
    return "Ready to upload to the selected advertiser's Creative Studio.";
  }
  return "";
}

export default describeUploadStage;
